import React from 'react';
import { Box, Text, Flex, Badge, Tooltip } from '@chakra-ui/react';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

/**
 * ChatBubble Component
 * Displays a single message in chat format (similar to WeChat/WhatsApp)
 *
 * @param {Object} props
 * @param {string} props.role - 'user' | 'assistant' | 'tool'
 * @param {string} props.content - Message content
 * @param {string} props.timestamp - Message timestamp
 * @param {string} props.intent - Detected intent (optional)
 * @param {Object} props.metadata - Additional metadata (optional)
 * @param {boolean} props.isEditable - Whether this message can be edited
 * @param {Function} props.onEdit - Callback when clicking editable message
 */
const ChatBubble = ({
  role = 'assistant',
  content,
  timestamp,
  intent,
  metadata,
  isEditable = false,
  onEdit,
}) => {
  const isUser = role === 'user';
  const isTool = role === 'tool';
  const nodeId = metadata?.node_id;
  const toolName = metadata?.tool_name;
  const bubbleBg = isUser ? 'chat.userBubble' : (isTool ? 'purple.100' : 'chat.aiBubble');
  const bubbleColor = isUser ? 'chat.userText' : (isTool ? 'purple.900' : 'chat.aiText');

  const handleClick = () => {
    if (isEditable && onEdit && nodeId) {
      onEdit(nodeId, content, metadata);
    }
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      mb={3}
      display="flex"
      justifyContent={isUser ? 'flex-start' : 'flex-end'}
    >
      <Box maxW="70%" minW="120px">
        {/* Message Bubble */}
        <Box
          bg={bubbleBg}
          color={bubbleColor}
          px={4}
          py={3}
          borderRadius="xl"
          boxShadow="chat"
          cursor={isEditable ? 'pointer' : 'default'}
          transition="all 0.2s"
          _hover={isEditable ? {
            boxShadow: 'chatHover',
            transform: 'translateY(-1px)',
            bg: isUser ? 'brand.blue.600' : (isTool ? 'purple.200' : 'gray.100'),
          } : {}}
          onClick={handleClick}
          position="relative"
        >
          {/* Editable indicator */}
          {isEditable && (
            <Badge
              position="absolute"
              top="-8px"
              right="-8px"
              colorScheme="green"
              fontSize="xs"
              borderRadius="full"
              px={2}
            >
              Editable
            </Badge>
          )}

          {isTool && (
            <Badge
              mb={2}
              colorScheme="purple"
              variant="solid"
              fontSize="xs"
            >
              {toolName ? `Tool: ${toolName}` : 'Tool Call'}
            </Badge>
          )}

          {/* Message Content */}
          <Text
            fontSize="md"
            lineHeight="1.5"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {content}
          </Text>

          {/* Intent Badge (for AI messages) */}
          {!isUser && !isTool && intent && (
            <Badge
              mt={2}
              colorScheme="blue"
              variant="subtle"
              fontSize="xs"
            >
              Intent: {intent}
            </Badge>
          )}

          {/* Node Trace (if available) */}
          {nodeId && (
            <Tooltip label={`Node: ${nodeId}`} placement="top">
              <Badge
                mt={2}
                ml={intent ? 2 : 0}
                colorScheme="purple"
                variant="subtle"
                fontSize="xs"
              >
                #{nodeId.slice(0, 8)}
              </Badge>
            </Tooltip>
          )}
        </Box>

        {/* Timestamp */}
        <Flex
          mt={1}
          justifyContent={isUser ? 'flex-start' : 'flex-end'}
          px={2}
        >
          <Text
            fontSize="xs"
            color="chat.timestamp"
          >
            {timestamp}
          </Text>
        </Flex>
      </Box>
    </MotionBox>
  );
};

export default ChatBubble;
